import React, { useState, useEffect } from 'react';
import { Volume2, VolumeX, Play } from 'lucide-react';

const logoUrl = '/logo.png';

interface SpeechMikeMockupProps {
  isDictating: boolean;
  showOverlay?: boolean;
  showPostDemo?: boolean;
  isMuted?: boolean;
  onStartDemo?: () => void;
  onShowChooser?: () => void;
  onToggleMute?: () => void;
  onStop?: () => void;
}

export const SpeechMikeMockup: React.FC<SpeechMikeMockupProps> = ({
  isDictating,
  showOverlay = false,
  showPostDemo = false,
  isMuted = false,
  onStartDemo,
  onShowChooser,
  onToggleMute,
  onStop,
}) => {
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    if (isDictating) {
      setShowHint(true);
      const timer = setTimeout(() => setShowHint(false), 5000);
      return () => clearTimeout(timer);
    } else {
      setShowHint(false);
    }
  }, [isDictating]);

  return (
    <div
      className="w-[375px] h-full bg-[#f7f7f7] rounded-[54px] shadow-[0_30px_60px_rgba(0,0,0,0.15)] border-[10px] border-slate-200 flex flex-col relative overflow-hidden"
      style={{ fontFamily: 'Lato, sans-serif' }}
    >
      {/* Initial Overlay */}
      {showOverlay && !showPostDemo && (
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-50 flex flex-col items-center justify-center p-8">
          <img src={logoUrl} alt="Commure Logo" className="h-12 w-auto object-contain mb-6" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Commure Dictation</h2>
          <p className="text-slate-500 text-center mb-10 text-sm">Experience the future of clinical documentation.</p>
          <div className="w-full space-y-4">
            <button onClick={onShowChooser ?? onStartDemo} className="w-full bg-[#1132ee] text-white py-4 rounded-xl font-semibold text-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2">
              <Volume2 className="w-5 h-5" />
              <span>View Demo</span>
            </button>
            <p className="text-slate-400 text-xs text-center">Turn sound on to hear</p>
            <button onClick={onToggleMute} className={`w-full py-3 rounded-xl font-medium text-sm transition-colors flex items-center justify-center space-x-2 ${isMuted ? 'bg-slate-100 text-slate-500' : 'bg-green-50 text-green-700 border border-green-200'}`}>
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              <span>{isMuted ? 'Sound Off' : 'Sound On'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Post-Demo Overlay */}
      {showPostDemo && (
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-50 flex flex-col items-center justify-center p-8">
          <img src={logoUrl} alt="Commure Logo" className="h-12 w-auto object-contain mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-1">Thanks for watching!</h2>
          <p className="text-slate-500 text-center mb-8 text-sm">Ready to get started with Commure Dictation?</p>
          <div className="w-full space-y-3">
            <button className="w-full bg-[#1132ee] text-white py-4 rounded-xl font-semibold text-base hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 88 88" className="w-5 h-5 fill-current">
                <path d="M0 12.402l35.687-4.86.016 34.423-35.703.206zm35.67 33.529l.016 34.453L0 76.03V46.135zM40.336 6.36L87.868 0v41.207l-47.532.328zM87.868 46.24V88L40.336 81.33v-35.3z" />
              </svg>
              <span>Download for Windows</span>
            </button>
            <button onClick={onShowChooser ?? onStartDemo} className="w-full bg-white text-[#1132ee] border-2 border-[#1132ee] py-3 rounded-xl font-semibold text-base hover:bg-blue-50 transition-colors flex items-center justify-center space-x-2">
              <Play className="w-4 h-4" />
              <span>Watch Another Demo</span>
            </button>
          </div>
        </div>
      )}

      {/* Info pill — pinned to bottom, overlays SVG if needed */}
      <div className="absolute bottom-6 left-0 right-0 flex justify-center z-10 px-6">
        <p className="text-[11px] text-slate-500 text-center leading-relaxed px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full border border-slate-200 shadow-sm">
          Commure Dictation works with popular speech mics you already have, or your built-in PC mic.
        </p>
      </div>

      {/* SpeechMike Device — centered in whitespace */}
      {/* Header — sits above the speech bubble zone (bubble appears at ~110px) */}
      <div className="flex flex-col items-center gap-1 pt-6 pb-4 shrink-0">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Commure" className="h-6 w-auto object-contain" />
          <span className="text-[17px] font-bold text-slate-800" style={{ fontFamily: 'Lato, sans-serif' }}>Dictation</span>
        </div>
        <span className="text-[12px] text-slate-400 font-medium">USB or Integrated Mic</span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center gap-5" style={{ paddingBottom: '32px' }}>
        <div className="relative">
        {isDictating && onStop && (
          <button
            onClick={onStop}
            className="absolute z-10 rounded-xl cursor-pointer hover:bg-white/20 transition-colors"
            style={{ left: '33%', top: '21.7%', width: '34%', height: '8%' }}
          />
        )}
        {showHint && (
          <div className="absolute z-10 flex flex-col items-center pointer-events-none animate-pulse"
            style={{ left: '50%', transform: 'translateX(-50%)', top: '32%' }}>
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M6 0L0 8H12L6 0Z" fill="#1132ee" opacity="0.7"/></svg>
            <span className="text-[10px] font-semibold text-blue-500 bg-white/80 backdrop-blur-sm px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap">Tap to stop demo</span>
          </div>
        )}
        <svg width="247" height="520" viewBox="0 0 130 400" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="smShadow" x="-30%" y="-5%" width="160%" height="115%">
              <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#000" floodOpacity="0.35"/>
            </filter>
            <linearGradient id="smBody" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#333"/>
              <stop offset="45%" stopColor="#484848"/>
              <stop offset="100%" stopColor="#252525"/>
            </linearGradient>
            <linearGradient id="smGrip" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#222"/>
              <stop offset="50%" stopColor="#2e2e2e"/>
              <stop offset="100%" stopColor="#1a1a1a"/>
            </linearGradient>
            <linearGradient id="smBtn" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#5a5a5a"/>
              <stop offset="100%" stopColor="#333"/>
            </linearGradient>
            <linearGradient id="smWheel" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#4a4a4a"/>
              <stop offset="100%" stopColor="#1a1a1a"/>
            </linearGradient>
          </defs>

          {/* Main body shape */}
          <path d="M32 38 Q32 6 65 6 Q98 6 98 38 L98 335 Q98 380 65 380 Q32 380 32 335 Z"
            fill="url(#smBody)" filter="url(#smShadow)"/>

          {/* Left edge highlight */}
          <path d="M33 38 Q33 8 65 8 L65 200" stroke="#606060" strokeWidth="0.75" opacity="0.35"/>

          {/* Mic grille */}
          <path d="M42 38 Q42 14 65 14 Q88 14 88 38 L88 72 Q88 76 65 76 Q42 76 42 72 Z" fill="#111"/>
          {[0,1,2,3,4,5].map(row =>
            [0,1,2,3].map(col => (
              <circle key={`g${row}-${col}`} cx={51 + col * 9} cy={24 + row * 8} r="1.8" fill="#2a2a2a" opacity="0.9"/>
            ))
          )}
          {/* Inner grille ring */}
          <ellipse cx="65" cy="45" rx="16" ry="14" stroke="#222" strokeWidth="1" fill="none" opacity="0.5"/>

          {/* Red LED strip */}
          <rect x="42" y="73" width="46" height="5" rx="2.5"
            fill={isDictating ? '#ef4444' : '#7f1d1d'}
            style={{ filter: isDictating ? 'drop-shadow(0 0 5px #ef4444)' : 'none' }}/>

          {/* Main RECORD button */}
          <rect x="43" y="87" width="44" height="32" rx="10"
            fill={isDictating ? '#1132ee' : 'url(#smBtn)'}
            style={{ filter: isDictating ? 'drop-shadow(0 0 10px rgba(17,50,238,0.7))' : 'none' }}/>
          <rect x="45" y="89" width="40" height="11" rx="6" fill={isDictating ? '#4a6aff' : '#666'} opacity="0.4"/>
          <circle cx="65" cy="103" r="9" fill={isDictating ? 'white' : '#888'}/>
          {isDictating
            ? <rect x="61" y="99" width="8" height="8" rx="1.5" fill="#1132ee"/>
            : <circle cx="65" cy="103" r="5" fill="#555"/>
          }

          {/* Forward / Back nav buttons */}
          <rect x="43" y="130" width="19" height="18" rx="5" fill="url(#smBtn)"/>
          <path d="M48 139 L57 134 L57 144 Z" fill="#aaa"/>

          <rect x="68" y="130" width="19" height="18" rx="5" fill="url(#smBtn)"/>
          <path d="M82 139 L73 134 L73 144 Z" fill="#aaa"/>

          {/* F1 / F3 function buttons */}
          <rect x="43" y="158" width="19" height="15" rx="4" fill="url(#smBtn)"/>
          <text x="52" y="170" fontSize="7.5" fill="#999" textAnchor="middle" fontFamily="system-ui, sans-serif" fontWeight="600">F1</text>

          <rect x="68" y="158" width="19" height="15" rx="4" fill="url(#smBtn)"/>
          <text x="77" y="170" fontSize="7.5" fill="#999" textAnchor="middle" fontFamily="system-ui, sans-serif" fontWeight="600">F3</text>

          {/* Scroll wheel */}
          <circle cx="65" cy="215" r="24" fill="url(#smWheel)" stroke="#555" strokeWidth="1.5"/>
          <circle cx="65" cy="215" r="17" fill="#2a2a2a" stroke="#444" strokeWidth="0.5"/>
          <circle cx="65" cy="215" r="8" fill="#3a3a3a" stroke="#666" strokeWidth="1"/>
          {[0,1,2,3,4,5,6,7,8,9,10,11].map(i => {
            const a = (i * 30 * Math.PI) / 180;
            return <line key={i} x1={65 + 15 * Math.cos(a)} y1={215 + 15 * Math.sin(a)}
              x2={65 + 20 * Math.cos(a)} y2={215 + 20 * Math.sin(a)}
              stroke="#555" strokeWidth="1.5" strokeLinecap="round"/>;
          })}

          {/* Grip zone */}
          <path d="M35 250 L35 330 Q35 345 65 345 Q95 345 95 330 L95 250 Z" fill="url(#smGrip)"/>
          {[258, 266, 274, 282, 290, 298, 306, 314, 322].map(y => (
            <line key={y} x1="39" y1={y} x2="91" y2={y}
              stroke="#3a3a3a" strokeWidth="1.5" strokeDasharray="3 2.5" opacity="0.7"/>
          ))}

          {/* Cable taper + cable */}
          <path d="M50 345 Q50 368 65 370 Q80 368 80 345 Z" fill="#1e1e1e"/>
          <rect x="59" y="368" width="12" height="22" rx="6" fill="#151515"/>
          {/* Cord coils */}
          <path d="M65 390 Q72 394 65 398 Q58 402 65 406" stroke="#111" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
        </svg>
        </div>
      </div>
    </div>
  );
};
